import { getApiBaseUrl } from '../config/apiConfig';
import type {
  ApiConfig,
  ProfileApiClient,
  ProfileQuotaResponse,
  ProfileStorageData,
} from '../types/api';
import { isDevelopment } from '../utils/environment';
import { ApiError, BaseApiClient } from './apiClient';

/** Responses that mean "this user has nothing stored yet", not "the read failed". */
const PROFILE_ABSENT_STATUSES = new Set([204, 404]);

const EMPTY_PROFILE: ProfileStorageData = {
  displayName: '',
  gravatarEmail: '',
  defaultMeetingId: '',
  defaultMeetingPasscode: '',
};

/**
 * Service for profile data storage and retrieval
 */
export class ProfileService extends BaseApiClient implements ProfileApiClient {
  constructor(config?: Partial<ApiConfig>) {
    const isDevMode = isDevelopment();

    super({
      baseUrl: getApiBaseUrl(isDevMode),
      ...config,
    });
  }

  /**
   * Fetch profile data by item identifier
   */
  async fetch(item: string): Promise<string> {
    const response = await this.get<string>(`/profile/app/storage/${encodeURIComponent(item)}`);
    return response.data;
  }

  /**
   * Store profile data for an item identifier
   */
  async store(item: string, data: string): Promise<void> {
    await this.post<void>(`/profile/app/storage/${encodeURIComponent(item)}`, data);
  }

  /**
   * Delete profile data by item identifier
   */
  async deleteItem(item: string): Promise<string> {
    const response = await this.delete<string>(`/profile/app/storage/${encodeURIComponent(item)}`);
    return response.data;
  }

  /**
   * List all data items in profile storage
   */
  async list(): Promise<string[]> {
    const response = await this.get<string[]>(`/profile/app/storage-list`);
    return response.data;
  }

  /**
   * Get quota for profile storage
   */
  async getQuota(): Promise<ProfileQuotaResponse> {
    const response = await this.get<ProfileQuotaResponse>(`/profile/app/storage-quota`);
    return response.data;
  }

  /**
   * Read the stored `preferences` document, distinguishing "nothing stored yet"
   * (null) from a read that failed (throws). Callers that merge before writing
   * depend on that distinction: merging onto an assumed-empty document after a
   * transient failure is exactly what drops existing fields.
   */
  private async readStoredProfile(): Promise<ProfileStorageData | null> {
    let raw: string;
    try {
      raw = await this.fetch('preferences');
    } catch (error) {
      if (error instanceof ApiError && PROFILE_ABSENT_STATUSES.has(error.status)) {
        return null;
      }
      throw error;
    }

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as ProfileStorageData;
    } catch {
      console.warn('[ProfileService] Stored preferences are not valid JSON; treating as empty');
      return null;
    }
  }

  /**
   * Fetch profile data from profile/app/storage/preferences
   * Response: { displayName, gravatarEmail, defaultMeetingId, defaultMeetingPasscode }
   */
  async fetchProfileData(): Promise<ProfileStorageData> {
    try {
      return (await this.readStoredProfile()) ?? { ...EMPTY_PROFILE };
    } catch (error) {
      console.warn('[ProfileService] Failed to read profile preferences', error);
      return { ...EMPTY_PROFILE };
    }
  }

  /**
   * Merge a patch into profile/app/storage/preferences and persist the result.
   *
   * The document holds fields edited from unrelated screens — display name and
   * gravatar from Settings, default meeting id and passcode from Personal
   * Meetings — so a caller that writes only the fields it knows about would
   * erase the rest (SM-1168). Rethrows without writing when the current
   * document cannot be read.
   *
   * @returns the full document as persisted, for callers updating a cache.
   */
  async storeProfileData(patch: Partial<ProfileStorageData>): Promise<ProfileStorageData> {
    const existing = await this.readStoredProfile();
    const merged: ProfileStorageData = { ...EMPTY_PROFILE, ...existing, ...patch };
    await this.store('preferences', JSON.stringify(merged));
    return merged;
  }

  /**
   * Fetch TOS acceptance status (compatible with current Jitsi Meet format)
   * Returns: {"accepted":"2023-02-01T17:42:37.084Z"}
   */
  async fetchTosAcceptance(): Promise<{ accepted: string } | null> {
    try {
      const data = await this.fetch('tos');
      return JSON.parse(data);
    } catch (_error) {
      // Return null if not found (user hasn't accepted TOS yet)
      return null;
    }
  }

  /**
   * Store TOS acceptance (compatible with current Jitsi Meet format)
   * Stores: {"accepted":"2023-02-01T17:42:37.084Z"}
   */
  async storeTosAcceptance(acceptance: { accepted: string }): Promise<void> {
    await this.store('tos', JSON.stringify(acceptance));
  }

  /**
   * Clear TOS acceptance (remove acceptance from backend)
   */
  async clearTosAcceptance(): Promise<void> {
    await this.deleteItem('tos');
  }

  /**
   * Fetch TOS metadata (last modification date)
   * Endpoint: /tos/metadata.json (API URL is auto-derived from hostname)
   * Note: Uses getWithoutAuth to avoid CORS issues with Authorization header
   */
  async fetchTosMetadata(): Promise<{ modified: string }> {
    const response = await this.getWithoutAuth<{ modified: string }>('/tos/metadata.json');
    return response.data;
  }
}

// Create singleton instance
export const profileService = new ProfileService();
